// Detects node failures the autopilot cannot fix by rewriting code: a
// required node setting (hyperparameter) or workflow variable that the USER
// left empty AND that the node's code actually reads. For these, opening the
// AskCode fix loop just burns time probing — the only fix is the user typing
// the value — so the caller shows an info popup instead.
//
// To avoid false positives, a match requires ALL of:
//   1. requirement-sounding text ("... is required", "missing ...", etc.), AND
//   2. the error naming a setting/variable that is ACTUALLY empty right now, AND
//   3. the node's OWN CODE actually reading that setting/variable.
// Rule 3 is what separates "user must type a value" from "an upstream node
// should have populated this at runtime". Example: a Format CSV node reads
// inputs[0] and never touches `vars` — the Debtors variable it's blamed for is
// populated at runtime by an upstream Store Debtors node, so its static default
// is empty. Without rule 3 we'd nag the user to "set Debtors" when the real fix
// is autofixing this node's code — exactly the AskCode path we want to trigger.

var REQUIREMENT_TEXT_RE = /\b(is required|are required|required\b|missing|not set|is empty|was empty|must be (set|provided|filled)|not provided|no value)\b/i;

function isEmptyValue(value) {
  return value === undefined || value === null || String(value).trim() === '';
}

function escapeRegex(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Does the node's code READ this workflow variable via the `vars` argument
// (vars.Name or vars['Name'])? Returns true when the node has NO code to
// inspect, so built-in nodes keep the pre-existing behavior (rule 3 only
// tightens custom/agent code, never loosens it).
function codeReadsVariable(code, name) {
  if (!code) return true;
  var n = escapeRegex(name);
  var re = new RegExp('vars\\s*(?:\\.\\s*' + n + '\\b|\\[\\s*[\'"`]' + n + '[\'"`]\\s*\\])', 'i');
  return re.test(code);
}

// Does the node's code READ this hyperparameter via the `settings` argument
// (settings.key or settings['key'])? Same no-code fallback as above.
function codeReadsSetting(code, key) {
  if (!code) return true;
  var k = escapeRegex(key);
  var re = new RegExp('settings\\s*(?:\\.\\s*' + k + '\\b|\\[\\s*[\'"`]' + k + '[\'"`]\\s*\\])', 'i');
  return re.test(code);
}

// Find a hyperparameter NAMED in the message whose configured value is actually
// empty/null AND that the node's code reads. Returns the matched name, or null.
// Names under 3 chars are skipped (1-2 char labels match almost any message).
function findEmptyHyperparam(node, lowerMessage) {
  var params = (node && Array.isArray(node.hyperparameters)) ? node.hyperparameters : [];
  var paramValues = (node && node.paramValues && typeof node.paramValues === 'object') ? node.paramValues : {};
  var code = String(node && node.code || '');
  for (var i = 0; i < params.length; i += 1) {
    var p = params[i];
    if (!p || !p.key) continue;
    var names = [p.label, p.key].filter(Boolean).map(String);
    for (var n = 0; n < names.length; n += 1) {
      var name = names[n].trim();
      if (name.length < 3) continue;
      if (lowerMessage.indexOf(name.toLowerCase()) === -1) continue;
      if (!isEmptyValue(paramValues[p.key])) continue;
      if (!codeReadsSetting(code, p.key)) continue;
      return name;
    }
  }
  return null;
}

// Find a workflow variable (Variables node) NAMED in the message whose value is
// actually empty/null AND that the FAILED node's code reads via `vars`. Returns
// the matched variable name, or null.
function findEmptyVariable(node, allNodes, lowerMessage) {
  var nodes = Array.isArray(allNodes) ? allNodes : [];
  var code = String(node && node.code || '');
  for (var v = 0; v < nodes.length; v += 1) {
    var vn = nodes[v];
    if (!vn || vn.icon !== 'variables' || !vn.title) continue;
    var varName = String(vn.title).trim();
    if (varName.length < 3) continue;
    if (lowerMessage.indexOf(varName.toLowerCase()) === -1) continue;
    if (!isEmptyValue(vn.paramValues && vn.paramValues.value)) continue;
    if (!codeReadsVariable(code, varName)) continue;
    return varName;
  }
  return null;
}

// node:     the failed node ({ hyperparameters, paramValues, code, ... })
// allNodes: every node in the workflow (to find Variables nodes by title)
// message:  the failure text shown to the user
// Returns { category: 'hyperparam' | 'variable', name } or null.
function detectMissingUserInput(node, allNodes, message) {
  var text = String(message || '');
  if (!text || !REQUIREMENT_TEXT_RE.test(text)) return null;
  var lower = text.toLowerCase();

  // 1) An empty hyperparameter the node reads → user must fill the setting.
  var hp = findEmptyHyperparam(node, lower);
  if (hp) return { category: 'hyperparam', name: hp };

  // 2) An empty workflow variable the node reads → user must set it.
  var varName = findEmptyVariable(node, allNodes, lower);
  if (varName) return { category: 'variable', name: varName };

  return null;
}

// For a silent-error flag the CHECKER already classified as
// 'hyperparam'/'variable', decide whether it is genuinely a "user must fill an
// empty value" case. Unlike detectMissingUserInput this does NOT require
// requirement-sounding text — the checker's category is the classification; we
// only confirm the named param/variable is actually empty AND read by the code.
// When it is NOT (e.g. the value is populated at runtime by an upstream node, or
// the code never reads it), the caller should fall through to in-node autofix
// rather than nag the user with a popup they cannot act on.
function hasEmptyRequiredValue(node, allNodes, category, message) {
  var lower = String(message || '').toLowerCase();
  if (category === 'hyperparam') return !!findEmptyHyperparam(node, lower);
  if (category === 'variable') return !!findEmptyVariable(node, allNodes, lower);
  return false;
}

module.exports = {
  detectMissingUserInput: detectMissingUserInput,
  hasEmptyRequiredValue: hasEmptyRequiredValue
};
